// NOTE: Temporarily using local copy of this, while I work out some next-js issues
// It appears that Next.js's transpilation gets in the way. next-transpile-modules *should* solve it, but I can't get that to work.
// Need to get to the bottom of this later, but for now, keeping a copy of the type here is a workaround
// export * from '@toolpath/tools'

import { AssetType } from 'lib/assets/asset'
import { KindId, getDefaultConversionFactor, isKindNumeric } from './kind'

export type Metadata = {
  title: string
  inlineTitle?: string // Title when used in context with other fields and groups, typically a shorter title (like "X" instead of "X Length" if part of a "Lengths" group)
  description?: string // A longer description to be used for e.g. tooltips
  kind: KindId
  default_value?: any // Default value (mode agnostic)
  default_value_in?: any // Default value (for inch mode)
  default_value_mm?: any // Default value (for metric mode)
  callback?: (any) => any
  callbackDependencies?: string[]
  for_types?: string[] // generalize further (enabled)
  comment?: string // Developer facing comment

  enumValues?: EnumMetadata[] // for kind == "enum"
  itemMetadata?: Metadata[] // for kind == "array"
  assetType?: AssetType // for kind == "asset"
  objectType?: string // for kind == "object"

  // Presentation hints
  hidden?: boolean // Not shown in UI representations
  hideChildren?: true // For 'asset' don't show child properties
  inGroup?: boolean // Inside current group
  noLabel?: boolean // Don't show label (e.g. for name field). Only for "wide" fields (text, enums etc.)

  // Validation
  required?: boolean // Must output value, even if equal to default_value
  gt?: number | string // Must be greater-than value or other property
  gte?: number | string // Must be greater-than-or-equal to value or other property
  lt?: number | string // Must be less-than value or other property
  lte?: number | string // Must be less-than-or-equal to value or other property

  // Enabled state conditions. E.g.,
  // enabledWhen: { type: { in: [TypeEnum.OptA, TypeEnum.OptB] } }
  enabledWhen?: { [fieldName: string]: WhenConditition<any> }
}

export type MetadataWithId = {
  id: string
} & Metadata

export interface EnumMetadata {
  value: string | number
  title: string
}

export function getEnumMetadataFromMap<T extends Record<string, string>>(
  map: T,
): EnumMetadata[] {
  return Object.entries(map).map(([value, title]) => ({
    value,
    title,
  }))
}

export function getEnumTitle(metadata: Metadata, value) {
  return (
    metadata.enumValues?.find((entry) => entry.value == value)?.title || value
  )
}

export type WhenConditition<T> = {
  in?: T[] // Matches if the property's value is in the provided array.
  notIn?: T[] // Matches if the property's value is not in the provided array.
  eq?: T // Matches if the property's value equals the provided value.
  notEq?: T // Matches if the property's value does not equal the provided value.
}

type MetadataWithIdAndChildren = MetadataWithId & {
  children?: MetadataWithId[]
}

export function groupMetadata(
  items: readonly MetadataWithId[],
): MetadataWithIdAndChildren[] {
  const result: MetadataWithIdAndChildren[] = []
  let currentGroup: MetadataWithIdAndChildren | null = null
  let groupAdded = false // tracks if currentGroup has been pushed to result

  for (const item of items) {
    if (item.kind === 'group') {
      // Start a new group without adding it immediately
      currentGroup = { ...item, children: [] }
      groupAdded = false
    } else {
      if (item.inGroup !== false && currentGroup) {
        if (!groupAdded) {
          result.push(currentGroup)
          groupAdded = true
        }
        currentGroup.children!.push(item)
      } else {
        // Otherwise, push the item as a standalone entry.
        result.push(item)
        currentGroup = null
        groupAdded = false
      }
    }
  }

  return result
}

export function makeMetadataMap(
  metadata: readonly MetadataWithId[],
): Record<string, Metadata> {
  return metadata.reduce((acc, item) => {
    acc[item.id] = item as Metadata
    return acc
  }, {} as Record<string, Metadata>)
}

function matchesCondition<T>(value: T, condition: WhenConditition<T>): boolean {
  if (condition.in !== undefined) {
    if (!condition.in.includes(value)) {
      return false
    }
  }
  if (condition.notIn !== undefined) {
    if (condition.notIn.includes(value)) {
      return false
    }
  }
  if (condition.eq !== undefined) {
    if (value !== condition.eq) {
      return false
    }
  }
  if (condition.notEq !== undefined) {
    if (value === condition.notEq) {
      return false
    }
  }
  return true
}

export function isFieldEnabled(
  fieldMeta: Metadata,
  data: Record<string, any>,
): boolean {
  if (!fieldMeta.enabledWhen) {
    return true // always enabled
  }

  // Check each dependency condition in enabledWhen.
  for (const [dependencyField, condition] of Object.entries(
    fieldMeta.enabledWhen,
  )) {
    const dependencyValue = data[dependencyField]
    if (!matchesCondition(dependencyValue, condition)) {
      return false
    }
  }

  return true
}

export function filterEnabledMetadata(
  data: Record<string, any>,
  metadata: readonly MetadataWithId[],
) {
  return metadata.filter((m) => isFieldEnabled(m, data))
}

/**
 * Prepares asset data for storage. Fields are assumed to already be validated.
 */
export function prepareAssetData(
  source: Record<string, any>,
  metadata: readonly MetadataWithId[],
) {
  const enabledMetadata = filterEnabledMetadata(source, metadata)
  const data = {}
  for (const meta of enabledMetadata) {
    data[meta.id] = source[meta.id]
  }
  return data
}

export function getDefaultValue(metadata: Metadata, metric: boolean) {
  // Try default_value first, then mm/in depending on metric, finally use the reverse and convert
  let value = metadata.default_value
  if (value === undefined) {
    value = metric ? metadata.default_value_mm : metadata.default_value_in
  }
  if (value === undefined && isKindNumeric(metadata.kind)) {
    value = metric ? metadata.default_value_in : metadata.default_value_mm
    value = (getDefaultConversionFactor(metadata.kind, metric) || 1.0) * value
  }
  if (value === undefined) {
    if (metadata.required) {
      throw new Error(
        `No default value for required property '${metadata.title}'`,
      )
    }
  }
  return value
}

export function getDefaults<T>(
  metadatas: readonly MetadataWithId[],
  metric: boolean,
  initial: Partial<T> = {},
): T {
  const data = { ...initial } as T
  for (const metadata of metadatas) {
    if (!metadata.id) {
      continue // No id - maybe a group
    }
    if (data[metadata.id] !== undefined) {
      continue // Already set
    }

    data[metadata.id] = getDefaultValue(metadata, metric)
  }
  return data
}

export function convertToUnitSystem(
  data: Record<string, any>,
  metadata: readonly MetadataWithId[],
  toMetric: boolean,
) {
  const metamap = makeMetadataMap(filterEnabledMetadata(data, metadata))
  for (const key of Object.keys(data)) {
    const metaEntry = metamap[key]
    if (!metaEntry) {
      continue
    }
    /* This causes TS errors in registry.ts - I need to investigate
    if (metaEntry.kind == 'asset') {
      // recurse
      const registryEntry = ASSET_REGISTRY[metaEntry.assetType]
      console.log('registryEntry', registryEntry)
      convertToUnitSystem(data[key], registryEntry.metadata, toMetric)
    }
    */
    const factor = getDefaultConversionFactor(metaEntry.kind, toMetric)
    if (factor) {
      data[key] *= factor
    }
  }
  if ('unit' in data) {
    data.unit = toMetric ? 'mm' : 'in'
  }
}
